import { getI18nextInstance } from 'i18n';

import {
  PasswordRequirements,
  PasswordValidationKey,
  PasswordValidations
} from './types';

export const DEFAULT_MIN_LENGTH = 12;
export const DEFAULT_PASSWORD_REQUIREMENTS: PasswordRequirements = {
  isComplex: true,
  minLength: DEFAULT_MIN_LENGTH
};
const COMPLEX_PASSWORD_FORMAT =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d])./;

const hasValidLength = (value: string, minLength: number): boolean =>
  value.length >= minLength;
const hasOneUpperCaseLetter = (value: string): boolean => /[A-Z]/.test(value);
const hasOneLowerCaseLetter = (value: string): boolean => /[a-z]/.test(value);
const hasOneNumber = (value: string): boolean => /\d/.test(value);
const hasOneSymbol = (value: string): boolean => /[^a-zA-Z\d]/.test(value);
export const isPasswordContainsPartOfEmail = ({
  password,
  email
}: {
  password: string;
  email: string;
}): boolean => {
  for (let i = 0; i + 4 < email.length; i += 1) {
    if (
      password.toLowerCase().includes(email.substring(i, i + 5).toLowerCase())
    ) {
      return true;
    }
  }

  return false;
};

export const validatePasswordRequirements = ({
  value,
  email,
  passwordRequirements
}: {
  value: string;
  email: string;
  passwordRequirements: PasswordRequirements;
}): PasswordValidationKey | void => {
  const { minLength } = passwordRequirements;

  if (!value) {
    return PasswordValidationKey.Required;
  }
  if (!hasValidLength(value, minLength)) {
    return PasswordValidationKey.InvalidLength;
  }
  if (!hasOneUpperCaseLetter(value)) {
    return PasswordValidationKey.OneUpperCase;
  }
  if (!hasOneLowerCaseLetter(value)) {
    return PasswordValidationKey.OneLowerCase;
  }
  if (!hasOneNumber(value)) {
    return PasswordValidationKey.OneNumber;
  }
  if (!hasOneSymbol(value)) {
    return PasswordValidationKey.OneSymbol;
  }
  if (
    isPasswordContainsPartOfEmail({
      password: value,
      email
    })
  ) {
    return PasswordValidationKey.PartOfEmail;
  }
  if (!COMPLEX_PASSWORD_FORMAT.test(value)) {
    return PasswordValidationKey.InvalidFormat;
  }
  return undefined;
};

export const getDefaultPasswordValidations = (): PasswordValidations => {
  const { t } = getI18nextInstance();

  return [
    {
      name: PasswordValidationKey.InvalidLength,
      getLabel: ({ minLength }) =>
        t('forms.min_length', {
          minLength
        }),
      isValid: ({ value, passwordRequirements }) =>
        hasValidLength(
          value,
          passwordRequirements?.minLength || DEFAULT_MIN_LENGTH
        )
    },
    {
      name: PasswordValidationKey.OneUpperCase,
      getLabel: () => t('forms.one_upper_case'),
      isValid: ({ value }) => hasOneUpperCaseLetter(value)
    },
    {
      name: PasswordValidationKey.OneLowerCase,
      getLabel: () => t('forms.one_lower_case'),
      isValid: ({ value }) => hasOneLowerCaseLetter(value)
    },
    {
      name: PasswordValidationKey.OneNumber,
      getLabel: () => t('forms.one_number'),
      isValid: ({ value }) => hasOneNumber(value)
    },
    {
      name: PasswordValidationKey.OneSymbol,
      getLabel: () => t('forms.one_symbol'),
      isValid: ({ value }) => hasOneSymbol(value)
    },
    {
      name: PasswordValidationKey.PartOfEmail,
      getLabel: () => t('forms.part_of_email'),
      isValid: ({ value, email }) =>
        !!value &&
        !isPasswordContainsPartOfEmail({
          password: value,
          email
        })
    }
  ];
};
